import React from 'react';
import { AlertTriangle, Flag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * BlacklistAlert Component
 * Shows blacklist indicator for clients with appropriate tooltips based on user role
 */
const BlacklistAlert = ({ 
  client, 
  size = 'sm', 
  showTooltip = true,
  className = '',
  variant = 'triangle' // 'triangle' or 'flag'
}) => {
  const { userRole } = useAuth();
  
  // Don't show anything if client is not blacklisted
  if (!client?.isBlacklisted) {
    return null;
  }

  const isAdmin = userRole === 'admin';

  // Icon sizes
  const iconSizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4', 
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const iconSize = iconSizes[size] || iconSizes.sm;

  // Choose icon based on variant
  const IconComponent = variant === 'flag' ? Flag : AlertTriangle;

  // Tooltip content based on user role
  const getTooltipContent = () => {
    if (isAdmin && client.mostRecentIncidentMotivo) {
      return `Cliente en lista negra: ${client.mostRecentIncidentMotivo}`;
    } else {
      return 'Cliente en lista negra, consultar con administrador';
    }
  };

  const alertElement = (
    <IconComponent 
      className={`${iconSize} text-red-600 flex-shrink-0 ${className}`}
      aria-label="Cliente en lista negra"
    />
  );

  // If tooltip is disabled, return just the icon
  if (!showTooltip) {
    return alertElement;
  }

  // Return with tooltip
  return (
    <div className="group relative inline-block">
      {alertElement}
      
      {/* Tooltip */}
      <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        {getTooltipContent()}
        
        {/* Additional info for admins */}
        {isAdmin && client.activeIncidentsCount > 1 && (
          <div className="text-xs text-gray-300 mt-1">
            {client.activeIncidentsCount} incidentes activos
          </div>
        )}
        
        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

/**
 * BlacklistBadge Component
 * A larger, more prominent blacklist indicator for use in headers or important sections
 */
export const BlacklistBadge = ({ client, className = '' }) => {
  const { userRole } = useAuth();
  
  if (!client?.isBlacklisted) {
    return null;
  }

  const isAdmin = userRole === 'admin';

  return (
    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ${className}`}>
      <AlertTriangle className="h-3 w-3 mr-1" />
      <span>Lista Negra</span>
      {isAdmin && client.activeIncidentsCount > 1 && (
        <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-900 rounded-full text-xs">
          {client.activeIncidentsCount}
        </span>
      )}
    </div>
  );
};

/**
 * BlacklistWarningCard Component  
 * A prominent warning card for use in forms and detailed views
 */
export const BlacklistWarningCard = ({ 
  client, 
  className = '', 
  onClick = null,
  clickable = false,
  orderId = null 
}) => {
  const { userRole } = useAuth();
  
  if (!client?.isBlacklisted) {
    return null;
  }

  const isAdmin = userRole === 'admin';
  const canClick = clickable && isAdmin && client.mostRecentIncident?.id && onClick;

  const cardClasses = `bg-red-50 border border-red-200 rounded-md p-4 ${className} ${
    canClick ? 'cursor-pointer hover:bg-red-100 transition-colors duration-200' : ''
  }`;

  const handleClick = (e) => {
    e.preventDefault();
    if (canClick && onClick) {
      // Call onClick with incident ID and optional orderId (if provided)
      if (orderId) {
        onClick(client.mostRecentIncident.id, orderId);
      } else {
        onClick(client.mostRecentIncident.id);
      }
    }
  };

  const cardContent = (
    <div className="flex items-start">
      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
      <div className="flex-1">
        <h3 className="text-sm font-medium text-red-800">
          Cliente en Lista Negra
          {canClick && (
            <span className="ml-2 text-xs text-red-600 font-normal">
              (clic para ver detalles)
            </span>
          )}
        </h3>
        <div className="mt-2 text-sm text-red-700">
          {isAdmin ? (
            <>
              <p>
                <span className="font-medium">Último incidente:</span> {client.mostRecentIncidentMotivo || 'Sin detalles'}
              </p>
              {client.activeIncidentsCount > 1 && (
                <p className="mt-1">
                  <span className="font-medium">Total de incidentes activos:</span> {client.activeIncidentsCount}
                </p>
              )}
              <p className="mt-2 text-xs">
                Revisa los detalles del cliente antes de proceder con nuevas órdenes.
              </p>
            </>
          ) : (
            <p>
              Este cliente está marcado en la lista negra. Consulta con un administrador antes de proceder.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (canClick) {
    return (
      <div 
        className={cardClasses} 
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e);
          }
        }}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div className={cardClasses}>
      {cardContent}
    </div>
  );
};

export default BlacklistAlert;